import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  useWindowDimensions,
  Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import SearchBar from './SearchBar';
import colorsData from '../../data/colors.json';

interface ColorPickerProps {
  selectedColors: string[];
  onColorToggle: (color: string) => void;
  maxSelection?: number;
  /** When provided, search is controlled by parent (e.g. generator puts search at top) */
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  /** Limit visible grid to this many rows (e.g. 5); scroll visible when more. Undefined = use container max height. */
  maxVisibleRows?: number;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  selectedColors,
  onColorToggle,
  maxSelection: _maxSelection = 10,
  searchQuery: controlledSearchQuery,
  onSearchQueryChange,
  maxVisibleRows,
}) => {
  const { colors } = useTheme();
  const [internalSearch, setInternalSearch] = useState<string>('');
  const searchQuery =
    controlledSearchQuery !== undefined
      ? controlledSearchQuery
      : internalSearch;
  const setSearchQuery = onSearchQueryChange ?? setInternalSearch;
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const isMobile = Platform.OS !== 'web' || screenWidth < 768;
  const swatchSize = isMobile ? 44 : 52;
  const swatchGap = isMobile ? 10 : 12;
  const swatchRadius = 12;
  const containerMaxHeight =
    maxVisibleRows != null
      ? undefined
      : isMobile
        ? 340
        : Math.round(screenHeight * 0.8);
  // Exactly N full rows: N*swatch + (N-1)*gap between rows + top/bottom padding
  const gridMaxHeight =
    maxVisibleRows != null
      ? 24 + maxVisibleRows * swatchSize + (maxVisibleRows - 1) * swatchGap
      : undefined;

  // Color name mapping for search
  const getColorNameMapping = () => {
    return {
      '#000000': ['black'],
      '#FFFFFF': ['white'],
      '#FF0000': ['red'],
      '#00FF00': ['green', 'lime'],
      '#0000FF': ['blue'],
      '#FFFF00': ['yellow'],
      '#FF00FF': ['magenta', 'fuchsia'],
      '#00FFFF': ['cyan', 'aqua'],
      '#808080': ['gray', 'grey'],
      '#800000': ['maroon'],
      '#800080': ['purple'],
      '#FFA500': ['orange'],
      '#FFC0CB': ['pink'],
      '#A52A2A': ['brown'],
      '#008000': ['green'],
      '#000080': ['navy', 'navy blue'],
      '#FF1493': ['deep pink', 'pink'],
      '#DC143C': ['crimson', 'red'],
      '#FF6347': ['tomato', 'red'],
      '#00CED1': ['dark turquoise', 'cyan'],
      '#FFD700': ['gold', 'yellow'],
      '#4B0082': ['indigo', 'purple'],
      '#32CD32': ['lime green', 'green'],
      '#FF4500': ['orange red', 'orange'],
      '#8B0000': ['dark red', 'red'],
      '#0000CD': ['medium blue', 'blue'],
      '#8B008B': ['dark magenta', 'purple'],
      '#B8860B': ['dark goldenrod', 'yellow'],
      '#228B22': ['forest green', 'green'],
      '#CD853F': ['peru', 'brown'],
      '#C0C0C0': ['silver'],
      '#008080': ['teal'],
    };
  };

  // Get all colors from JSON file
  const getAllColors = () => {
    return colorsData.categories;
  };

  // Pills: All, Primary, then Red, Orange, Yellow, Green, Blue, Violet (violet = purple in data)
  const filterChips = useMemo(
    () => [
      { key: 'all', label: 'All' },
      { key: 'primary', label: 'Primary' },
      { key: 'red', label: 'Red' },
      { key: 'orange', label: 'Orange' },
      { key: 'yellow', label: 'Yellow' },
      { key: 'green', label: 'Green' },
      { key: 'blue', label: 'Blue' },
      { key: 'violet', label: 'Violet' },
    ],
    []
  );

  // Pill bg colors when selected (contrast text = white for all)
  const PILL_ACTIVE_COLORS: Record<string, string> = {
    red: '#CC0000',
    orange: '#FF8C00',
    yellow: '#D4A017',
    green: '#228B22',
    blue: '#0066CC',
    violet: '#8F00FF',
  };

  const getPillStyle = (key: string, isActive: boolean) => {
    if (!isActive) {
      return {
        backgroundColor: colors.border + '50',
        borderColor: colors.border,
        color: colors.text,
      };
    }
    const bg = PILL_ACTIVE_COLORS[key] ?? colors.primary;
    const textColor = PILL_ACTIVE_COLORS[key] ? '#FFFFFF' : colors.background;
    return { backgroundColor: bg, borderColor: bg, color: textColor };
  };

  // Filter colors based on search and quick filter
  const getFilteredColors = useMemo(() => {
    const allColors = getAllColors();
    const colorNameMap = getColorNameMapping();
    let filtered: { category: string; colors: string[] }[] = [];

    if (categoryFilter === 'all') {
      Object.entries(allColors).forEach(([category, colorList]) => {
        filtered.push({ category, colors: colorList });
      });
    } else {
      // Violet pill uses 'purple' category from data
      const dataKey = categoryFilter === 'violet' ? 'purple' : categoryFilter;
      filtered.push({
        category: categoryFilter,
        colors: allColors[dataKey as keyof typeof allColors] || [],
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered
        .map(({ category, colors }) => ({
          category,
          colors: colors.filter((color) => {
            if (color.toLowerCase().includes(query)) return true;
            if (category.toLowerCase().includes(query)) return true;
            const colorNames = colorNameMap[color as keyof typeof colorNameMap];
            if (
              colorNames &&
              colorNames.some((name) => name.toLowerCase().includes(query))
            )
              return true;
            return false;
          }),
        }))
        .filter(({ colors }) => colors.length > 0);
    }

    return filtered;
  }, [searchQuery, categoryFilter]);

  return (
    <View
      style={[
        styles.container,
        isMobile && styles.containerMobile,
        { backgroundColor: colors.card },
        containerMaxHeight != null && { maxHeight: containerMaxHeight },
      ]}
    >
      {/* Search - only when not controlled (parent shows search at top) */}
      {controlledSearchQuery === undefined && (
        <SearchBar
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder="Search colours or hex"
          containerStyle={styles.searchContainer}
          showClearButton={true}
          onClear={() => setSearchQuery('')}
          size="compact"
        />
      )}

      {/* Quick filter chips — All, Primary, Red, Orange, Yellow, Green, Blue, Violet */}
      <View style={styles.filterChipsWrap}>
        {filterChips.map(({ key, label }) => {
          const isActive = categoryFilter === key;
          const {
            backgroundColor,
            borderColor,
            color: textColor,
          } = getPillStyle(key, isActive);
          return (
            <TouchableOpacity
              key={key}
              style={[styles.filterChip, { backgroundColor, borderColor }]}
              onPress={() => setCategoryFilter(key)}
              activeOpacity={0.8}
            >
              <Text
                style={[styles.filterChipText, { color: textColor }]}
                numberOfLines={1}
              >
                {label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Color grid — maxVisibleRows caps height so ~5 rows show with scroll */}
      <ScrollView
        style={[
          styles.colorGridScroll,
          { borderWidth: 1, borderColor: colors.border, borderRadius: 12 },
          gridMaxHeight != null && { maxHeight: gridMaxHeight },
        ]}
        contentContainerStyle={styles.colorGridContent}
        showsVerticalScrollIndicator={true}
        nestedScrollEnabled={true}
      >
        {getFilteredColors.length === 0 ||
        getFilteredColors.every(({ colors }) => colors.length === 0) ? (
          <View style={styles.emptyState}>
            <Ionicons
              name="color-filter-outline"
              size={48}
              color={colors.secondary}
            />
            <Text style={[styles.emptyText, { color: colors.text }]}>
              No colors found
            </Text>
            <Text style={[styles.emptySubtext, { color: colors.secondary }]}>
              Try another filter or search
            </Text>
          </View>
        ) : (
          <View style={[styles.colorGrid, { gap: swatchGap }]}>
            {getFilteredColors.map(({ category, colors: colorList }) => (
              <React.Fragment key={category}>
                {colorList.map((color, index) => {
                  const isSelected = selectedColors.includes(color);
                  return (
                    <Pressable
                      key={`${category}-${index}`}
                      style={({ pressed }) => [
                        styles.colorOption,
                        {
                          width: swatchSize,
                          height: swatchSize,
                          borderRadius: swatchRadius,
                          backgroundColor: color,
                          borderColor: isSelected
                            ? colors.primary
                            : 'rgba(0,0,0,0.12)',
                          borderWidth: isSelected ? 3 : 1,
                          opacity: pressed ? 0.85 : 1,
                          transform: [{ scale: pressed ? 0.94 : 1 }],
                        },
                      ]}
                      onPress={() => onColorToggle(color)}
                    >
                      {isSelected && (
                        <View
                          style={[
                            styles.selectedIndicator,
                            { borderRadius: swatchRadius / 2 },
                          ]}
                        >
                          <Ionicons
                            name="checkmark"
                            size={swatchSize * 0.35}
                            color="white"
                          />
                        </View>
                      )}
                    </Pressable>
                  );
                })}
              </React.Fragment>
            ))}
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    paddingTop: 12,
    borderRadius: 12,
    overflow: 'hidden',
    gap: 12,
  },
  containerMobile: {
    padding: 0,
  },
  searchContainer: {
    padding: 0,
    marginBottom: 0,
  },
  filterChipsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingVertical: 4,
  },
  filterChip: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 20,
    borderWidth: 1,
  },
  filterChipText: {
    fontSize: 13,
    fontWeight: '600',
  },
  colorGridScroll: {
    flex: 1,
    width: '100%',
    minHeight: 0,
  },
  colorGridContent: {
    width: '100%',
    paddingBottom: 8,
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignContent: 'center',
    paddingHorizontal: 8,
    paddingTop: 10,
    paddingBottom: 14,
  },
  colorOption: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 3,
    elevation: 2,
  },
  selectedIndicator: {
    position: 'absolute',
    top: 4,
    left: 4,
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.65)',
    zIndex: 10,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
});

export default ColorPicker;
